import { useEffect, useState, type FormEvent } from 'react';
import type { MarketplaceProduct } from '@isbd/shared';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/features/ui/toast/useToast';
import { trackConversion } from '@/features/analytics/tracker';

type Props = {
  product: MarketplaceProduct;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function ContactSellerDialog({ product, open, onOpenChange }: Props) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [residence, setResidence] = useState('');
  const [phone, setPhone] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  function reset() {
    setSent(false);
    setName('');
    setEmail('');
    setResidence('');
    setPhone('');
    setWhatsapp('');
    setMessage('');
  }

  function close() {
    onOpenChange(false);
    window.setTimeout(reset, 200);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSending(true);

    const { error: err } = await supabase.rpc('submit_marketplace_inquiry', {
      p_product_id: product.id,
      p_visitor_name: name.trim(),
      p_visitor_email: email.trim(),
      p_residence: residence.trim(),
      p_phone: phone.trim(),
      p_whatsapp: whatsapp.trim(),
      p_message: message.trim(),
    });

    setSending(false);
    if (err) {
      toast.error(err.message);
      return;
    }

    setSent(true);
    trackConversion('marketplace_inquiry', { product_id: product.id }, `/marketplace/${product.id}`);
  }

  if (!open) return null;

  return (
    <div className="mp-modal-backdrop" role="presentation" onClick={close}>
      <div
        className="mp-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mp-contact-title"
        onClick={(ev) => ev.stopPropagation()}
      >
        <button type="button" className="mp-modal-close" onClick={close} aria-label="Fermer">
          ×
        </button>
        <h2 id="mp-contact-title">Contacter le vendeur</h2>
        <p className="mp-modal-lead">
          Exprimez votre besoin à {product.seller_name} au sujet de « {product.name} ».
        </p>

        {sent ? (
          <div className="mp-sent">
            <div className="mp-sent-icon" aria-hidden>
              ✓
            </div>
            <h3>Demande envoyée !</h3>
            <p>Le vendeur vous répondra dans les plus brefs délais.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {(product.seller_email || product.seller_phone) && (
              <div className="mp-contact-meta">
                {product.seller_email ? <div>✉ {product.seller_email}</div> : null}
                {product.seller_phone ? <div>☎ {product.seller_phone}</div> : null}
              </div>
            )}
            <div className="mp-field">
              <label htmlFor="mp-name">Votre nom</label>
              <input id="mp-name" required value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="mp-field">
              <label htmlFor="mp-email">Votre email</label>
              <input
                id="mp-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="mp-field">
              <label htmlFor="mp-residence">Votre lieu de résidence</label>
              <input
                id="mp-residence"
                required
                placeholder="Ville, Pays"
                value={residence}
                onChange={(e) => setResidence(e.target.value)}
              />
            </div>
            <div className="mp-field">
              <label htmlFor="mp-phone">Votre contact téléphonique</label>
              <input
                id="mp-phone"
                type="tel"
                required
                placeholder="+225 07 00 00 00"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
            <div className="mp-field">
              <label htmlFor="mp-whatsapp">Votre contact WhatsApp</label>
              <input
                id="mp-whatsapp"
                type="tel"
                placeholder="+225 07 00 00 00"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
              />
            </div>
            <div className="mp-field">
              <label htmlFor="mp-message">Expression de besoin</label>
              <textarea
                id="mp-message"
                required
                rows={4}
                placeholder="Décrivez votre besoin..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>
            <button className="btn btn-primary btn-block" type="submit" disabled={sending}>
              {sending ? 'Envoi…' : 'Envoyer ma demande'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
